import { useState, useMemo, useEffect } from 'react'
import classNames from 'classnames'
import useViewport from '../../../../../hooks/useViewport'
import { Box, useTheme } from '@homebotapp/hb-react-component-catalog'
import { ListingCompact } from '../../../../../api/gqlaxy/gql/generated/graphql'
import { ListingCardNew } from '../../../ListingCardNew/ListingCardNew'
import { useMarkerColors } from './hooks/useMarkerColors'
import { ListingStatusEnum } from '../../../../../types/listingStatus'
import { isListingSeen } from '../utils/seenListings'

import styles from './Marker.module.scss'
import { formatAbbrCurrency } from '../../../../../helpers/formatters'

export interface MarkerProps {
  isFullScreen: boolean
  listing: ListingCompact
  selectedListingId: string
  onClick: (listing: ListingCompact) => void
}

export const Marker = ({ isFullScreen, listing, selectedListingId, onClick }: MarkerProps) => {
  const { theme } = useTheme()
  const { isMediumOrAbove } = useViewport()
  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 })
  const [isActive, setIsActive] = useState(false)
  const isLightThemeType = theme.isLightThemeType
  const isDarkThemeType = !isLightThemeType

  const isSeen = useMemo(() => isListingSeen(listing.id), [listing.id])

  // Reset active state when this marker is no longer selected
  useEffect(() => {
    if (listing.id !== selectedListingId) {
      setIsActive(false)
    }
  }, [selectedListingId, listing.id])

  const { colors, onMouseEnter, onMouseLeave } = useMarkerColors({
    status: listing.status as ListingStatusEnum,
    isViewed: isSeen,
    isSelected: listing.id === selectedListingId,
    isActive
  })

  const markerClassName = classNames(styles.baseMarker, {
    [styles.light]: isLightThemeType,
    [styles.dark]: isDarkThemeType,
    [styles.viewed]: isSeen,
    [styles.selected]: listing.id === selectedListingId,
    [styles.active]: isActive
  })

  const getListingCardTransform = () => {
    const height = window.innerHeight

    if (clickPosition.y < height / 2) {
      return 'scale(0.75)translateX(-55%)translateY(-15px)'
    } else {
      return 'scale(0.75)translateX(-55%)translateY(-125%)'
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    setClickPosition({ x: e.clientX, y: e.clientY })
    setIsActive(true)
    onClick(listing)
  }

  const handleFocus = () => {
    setIsActive(true)
  }

  const handleBlur = () => {
    setIsActive(false)
  }

  const handleMouseEnter = () => {
    onMouseEnter()
  }

  const handleMouseLeave = () => {
    onMouseLeave()
    setIsActive(false)
  }

  return (
    <>
      {selectedListingId === listing?.id && (!isMediumOrAbove ? isFullScreen : true) && (
        <Box position='absolute' zIndex={10} w='300px' transform={getListingCardTransform()}>
          <ListingCardNew listing={listing} />
        </Box>
      )}
      <div
        className={markerClassName}
        style={{
          backgroundColor: colors.backgroundColor,
          borderColor: colors.borderColor,
          color: colors.textColor
        }}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        role='button'
        tabIndex={0}
      >
        {formatAbbrCurrency(listing?.priceCents / 100)}
      </div>
    </>
  )
}
